/**
 * Medleve organic SEO/GEO foundation — site-level constants.
 *
 * Nothing in `src/modules/medleveSeo` may be imported by paid/campaign ("frozen") routes.
 * Every value below is either stated in the PRD or already present in this repository.
 */
import { MEDLEVE_SUPPORT_EMAIL } from 'constants/omensSupportContact'

/** Canonical origin. Never derive it from the request host (apex and other brand hosts serve the same app). */
export const MEDLEVE_ORIGIN = 'https://www.medleve.com'

export const MEDLEVE_SITE_NAME = 'Medleve'
export const MEDLEVE_LANGUAGE = 'pt-BR'
export const MEDLEVE_OG_LOCALE = 'pt_BR'

/** PRD §4 — institutional definition. */
export const MEDLEVE_ORGANIZATION_DESCRIPTION =
  'A Medleve é uma plataforma brasileira de saúde digital que conecta pacientes a médicos para avaliação e acompanhamento de tratamentos para obesidade, sobrepeso e saúde metabólica.'

/**
 * PRD §5 — legal entity. REQUIRES LEGAL REVIEW before any change.
 */
export const MEDLEVE_LEGAL_ENTITY = {
  legalName: 'OMENS SERVIÇOS ONLINE E SOFTWARE LTDA.',
  cnpj: '38.143.501/0001-08'
} as const

/**
 * Official profiles from Consolidado §7, without share/tracking query strings.
 * The paid landing footer retains its own frozen profile list.
 */
export const MEDLEVE_SAME_AS: readonly string[] = [
  'https://www.instagram.com/medleve.br/',
  'https://www.linkedin.com/company/medlevebr',
  'https://youtube.com/@medlevebrasil',
  'https://www.facebook.com/profile.php?id=61577974430530',
  'https://www.tiktok.com/@medleve.br'
]

/**
 * Organization logo (PRD §51): square 512x512 PNG rendered from the Medleve logo artwork the site already
 * uses (`components/svg/MedleveLogo`). Replace the file if brand supplies an official logo asset.
 */
export const MEDLEVE_LOGO_PATH: string | undefined = '/landings/medleve/medleve-logo.png'

/** Default Open Graph / Twitter share image of Medleve organic pages (1200×630). */
export const MEDLEVE_DEFAULT_OG_IMAGE: { path: string; width?: number; height?: number; alt?: string } | undefined = {
  path: '/landings/medleve/medleve-og.png',
  width: 1200,
  height: 630,
  alt: 'Medleve'
}

/**
 * Organization contactPoint: the public support e-mail already shown in the live Medleve footer
 * (`MEDLEVE_SUPPORT_EMAIL`, layouts/LadingLayout/components/Footer/MedleveFooter.tsx).
 * `telephone` is omitted on purpose: the only Medleve number in the repo is a WhatsApp chat link,
 * not a confirmed voice line.
 */
export const MEDLEVE_CONTACT_POINT:
  | { contactType: string; email?: string; telephone?: string; url?: string }
  | undefined = { contactType: 'customer support', email: MEDLEVE_SUPPORT_EMAIL }
